import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import OnboardingModal from '../components/OnboardingModal';
import { DASHBOARD } from '../constants/strings';
import { formatTime } from '../utils/time';
import {
  IconNavCamera, IconNavCalendar, IconDelete, IconCheck, IconClose,
  IconClock, IconUsers, IconChefHat, IconChevRight,
} from '../components/icons';
import './RecipeList.css';
import './Dashboard.css';

function greeting() {
  const h = new Date().getHours();
  if (h < 11) return DASHBOARD.greetingMorning;
  if (h < 17) return DASHBOARD.greetingDay;
  if (h < 22) return DASHBOARD.greetingEvening;
  return DASHBOARD.greetingNight;
}

function greetingSub() {
  const h = new Date().getHours();
  if (h < 11) return DASHBOARD.subMorning;
  if (h < 17) return DASHBOARD.subDay;
  if (h < 22) return DASHBOARD.subEvening;
  return DASHBOARD.subNight;
}

function formatDate() {
  return new Date().toLocaleDateString('de-AT', {
    weekday: 'long', day: 'numeric', month: 'long',
  });
}

const COOKING_TIPS = [
  'Fleisch vor dem Braten immer auf Raumtemperatur bringen — so wird es gleichmäßiger gar.',
  'Pasta-Wasser salzen wie das Meer: mindestens 10 g Salz pro Liter.',
  'Zwiebeln glasig dünsten dauert 8–10 Minuten — Geduld zahlt sich aus.',
  'Zitronensaft zum Schluss: Hitze zerstört das Aroma, kalt zugeben.',
  'Schneidebrett nach Fleisch immer sofort mit heißem Wasser reinigen.',
  'Kräuter wie Basilikum niemals mitkochen — erst auf dem Teller.',
  'Eier für perfekte Spiegeleier in einer kalten Pfanne starten.',
  'Knoblauch verbrennt leicht — erst zugeben wenn die Zwiebel fertig ist.',
  'Reste-Trick: Parmesan-Rinde in Suppen mitkochen für mehr Tiefe.',
  'Teig für Palatschinken mindestens 30 Minuten rasten lassen.',
  'Salat erst direkt vor dem Servieren dressieren, sonst wird er weich.',
  'Butter bräunen (Nussbutter) statt schmelzen: gibt Saucen ein nussiges Aroma.',
  'Schnitzel nur einmal wenden — zu oft anfassen macht die Panade weich.',
  'Tomaten nie im Kühlschrank lagern — sie verlieren ihr Aroma.',
  'Beim Kochen von Hülsenfrüchten erst am Ende salzen, sonst bleiben sie hart.',
  'Ofen immer vorheizen — 10 Minuten reichen meist aus.',
  'Scharfes Messer ist sicherer als ein stumpfes: mehr Kontrolle, weniger Kraft.',
  'Gulasch am Tag danach schmeckt besser — Aromen brauchen Zeit.',
  'Kartoffeln für Knödel am Vortag kochen und ausdampfen lassen.',
  'Strudel-Teig: so dünn ausziehen, dass man eine Zeitung durchlesen kann.',
  'Topfen vor dem Verarbeiten abtropfen lassen — sonst wird der Teig zu nass.',
  'Rindssuppe: immer kalt aufsetzen, langsam erhitzen für klare Brühe.',
  'Semmelknödel im siedenden, nicht sprudelnden Wasser garziehen lassen.',
  'Gewürze in einer trockenen Pfanne kurz anrösten — entfaltet die Aromen.',
  'Sauce zu viel Salz? Eine rohe Kartoffel 10 Minuten mitköcheln lässt sie ziehen.',
  'Frische Kräuter im feuchten Tuch im Kühlschrank bleiben tagelang frisch.',
  'Braten vor dem Anschneiden 10 Minuten rasten lassen — Saft bleibt drin.',
  'Knödel immer zuerst einen Probe-Knödel kochen bevor du alle machst.',
  'Mehl für Saucen immer zuerst in Butter anschwitzen — kein Mehlgeschmack.',
  'Apfelstrudel-Teig: Olivenöl macht ihn geschmeidig und reißfest.',
];

function getDailyTip() {
  const day = Math.floor(Date.now() / 86400000);
  return COOKING_TIPS[day % COOKING_TIPS.length];
}

export default function Dashboard() {
  const { user } = useAuth();
  const [recentRecipes, setRecentRecipes]   = useState([]);
  const [newestRecipes, setNewestRecipes]   = useState([]);
  const [recipeCount, setRecipeCount]       = useState(null);
  const [loading, setLoading]               = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showDemoPrio, setShowDemoPrio]     = useState(false);
  const [expiringItems, setExpiringItems]   = useState(null);

  const isGuest = !user;

  useEffect(() => {
    if (user !== null) {
      setShowOnboarding(!user.onboarding_confirmed_at);
    }
  }, [user]);
  useEffect(() => {
    async function load() {
      try {
        if (isGuest) {
          const data = await api.getPublicRecipes({ limit: 10 });
          const recipes = data.recipes || [];
          setRecentRecipes(recipes.slice(0, 7));
          setRecipeCount(data.total ?? recipes.length);
          setNewestRecipes([]);
        } else {
          const [myData, newestData, expiringData] = await Promise.all([
            api.getRecipes({ limit: 7, my: true }),
            api.getRecipes({ limit: 10, public_only: true }),
            api.getFridgeExpiring().catch(() => ({ items: [] })),
          ]);
          setExpiringItems(expiringData.items || []);
          const myRecipes = myData.recipes || myData || [];
          setRecentRecipes(myRecipes.slice(0, 7));
          setRecipeCount(myData.total ?? myRecipes.length);
          setNewestRecipes((newestData.recipes || newestData || []).slice(0, 10));
        }
      } catch {
        // Dashboard ist nicht kritisch – kein harter Fehler
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isGuest]);

  const featured    = recentRecipes[0] || null;
  const recommended = recentRecipes.slice(1, 4);

  return (
    <div className="dashboard">

      {isGuest && (
        <div style={{
          background: 'var(--green-pale, #e8f9f0)',
          border: '1px solid var(--green, #2da86a)',
          borderRadius: '10px',
          padding: '12px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <span style={{ color: 'var(--txt, #1a1a1a)', flex: 1 }}>
            👋 Du siehst eine Demo — melde dich an um deine eigenen Rezepte zu verwalten.
          </span>
          <a href="/login?tab=register" className="btn btn-primary btn-sm">Kostenlos registrieren</a>
        </div>
      )}

      {showOnboarding && <OnboardingModal onClose={() => setShowOnboarding(false)} />}

      {/* ── Eule-Greeting ── */}
      <div className="dash-eule-greeting">
        <div className="dash-eule-icon">🦉</div>
        <div className="dash-eule-text">
          <div className="dash-eule-hello">
            {greeting()}, <em>{isGuest ? 'Gast' : user?.username}.</em>
          </div>
          {!isGuest && user?.created_at && new Date(user.created_at) < new Date('2026-07-01') && (
            <span className="settings-beta-badge" style={{ marginTop: '6px', display: 'inline-flex' }}>
              🦉 Beta-Pionier
            </span>
          )}
          <div className="dash-eule-sub">{greetingSub()}</div>
          <div className="dash-eule-date">{formatDate()}</div>
        </div>
      </div>

      {/* ── Haupt-CTAs ── */}
      <div className="dash-main-cta">
        <Link to="/kuehlschrank" className="dash-cta-btn dash-cta-primary">
          <span className="dash-cta-icon"><IconNavCamera size={22} /></span>
          <span>
            <strong>{DASHBOARD.ctaPrimaryLabel}</strong>
            <small>{DASHBOARD.ctaPrimarySub}</small>
          </span>
        </Link>
        <Link to="/upload" className="dash-cta-btn dash-cta-secondary">
          <span className="dash-cta-icon"><IconChefHat size={22} /></span>
          <span>
            <strong>{DASHBOARD.ctaSecondaryLabel}</strong>
            <small>{DASHBOARD.ctaSecondarySub}</small>
          </span>
        </Link>
      </div>

      {/* ── Hero-Card (zuletzt hinzugefügt) ── */}
      {featured && (
        <Link
          to={`/recipe/${featured.id}`}
          className={`hero-card${featured.image_path ? ' hero-card--bg' : ''}`}
          style={featured.image_path ? { backgroundImage: `url(${featured.image_path})` } : {}}
        >
          <div className="hero-content">
            <div className="hero-eyebrow">
              <span className="live-dot-wrap"><span className="live-dot" /></span>
              {DASHBOARD.zuleztGerettet}
            </div>
            <div className="hero-title">{featured.title}</div>
            <div className="hero-meta">
              {featured.prep_time > 0 && (
                <span>
                  <IconClock size={14} />
                  {formatTime(featured.prep_time)}
                </span>
              )}
              {featured.servings && (
                <span>
                  <IconUsers size={14} />
                  {featured.servings} Portionen
                </span>
              )}
              {featured.category && <span>{featured.category}</span>}
            </div>
          </div>
          {!featured.image_path && <div className="hero-img"><IconChefHat size={48} /></div>}
        </Link>
      )}

      {/* ── Tipp des Tages ── */}
      <div className="dash-insight-card dash-insight-card--tip" style={{ marginBottom: 16 }}>
        <div className="di-label">{DASHBOARD.tippLabel}</div>
        <div className="di-tip">{getDailyTip()}</div>
      </div>

      {/* ── Stats ── */}
      {recipeCount !== null && (
        <div className="stats-strip">
          <div className="stat-card">
            <div className="stat-accent sa-g" />
            <div>
              <div className="stat-num">{recipeCount}</div>
              <div className="stat-lbl">{DASHBOARD.statLabel}</div>
            </div>
          </div>
          {/* TODO: Wochenplaner-Stat — wieder einblenden wenn Wochenplaner live */}
          <Link to="/kuehlschrank" className="stat-card">
            <div className="stat-accent sa-b" />
            <div>
              <div className="stat-num">
                {expiringItems === null ? '—' : expiringItems.length > 0 ? expiringItems.length : '✓'}
              </div>
              <div className="stat-lbl">{DASHBOARD.statAblaufend}</div>
            </div>
          </Link>
        </div>
      )}

      {/* ── 2-Spalten-Grid: Prio + Empfehlungen ── */}
      {!loading && (!isGuest || recentRecipes.length > 0) && (
        <div className="dash-grid">

          {/* Links: Verbrauchs-Priorität — nur für eingeloggte User */}
          {!isGuest && (
          <div>
            <div className="sec-header" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              <div className="sec-title">Verbrauchs-Priorität</div>
              <div style={{ display: 'flex', gap: 10, fontSize: '0.72rem', color: 'var(--txt3)' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--red)', display: 'inline-block' }} />Heute
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)', display: 'inline-block' }} />1–3 Tage
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--green)', display: 'inline-block' }} />4+ Tage
                </span>
              </div>
            </div>
            {expiringItems && expiringItems.length > 0 ? (
              <div className="prio-list">
                {expiringItems.slice(0, 3).map(({ id, name, days_left: days }) => {
                  const isToday      = days <= 0;
                  const isCrit       = days <= 2;
                  const urgencyClass = isToday ? 'prio-card--red' : days <= 3 ? 'prio-card--amber' : 'prio-card--green';
                  const pillClass    = isToday ? 'pill pill-solid prio-pill-today' : isCrit ? 'pill pill-red' : 'pill pill-amber';
                  const pillText     = isToday ? 'HEUTE!' : `NOCH ${days}T`;
                  return (
                    <div key={id} className={`prio-card ${urgencyClass}`}>
                      <div className="prio-normal">
                        <div className="prio-top">
                          <span className="prio-name">{name}</span>
                          <span className={pillClass} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{pillText}</span>
                        </div>
                      </div>
                      <div className="prio-actions">
                        <button className="pa-btn pa-done" title="Verbraucht"><IconCheck size={15} /></button>
                        <button className="pa-btn pa-cal" title="Zum Kalender"><IconNavCalendar size={15} /></button>
                        <button className="pa-btn pa-del" title="Entfernen"><IconDelete size={15} /></button>
                      </div>
                    </div>
                  );
                })}
                {expiringItems.length > 3 && (
                  <Link to="/kuehlschrank/inventar" className="sec-link" style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginTop: 4, fontSize: '0.8rem' }}>
                    Alle {expiringItems.length} ablaufenden anzeigen →
                  </Link>
                )}
                <Link to="/kuehlschrank" className="prio-add-btn" style={{ textDecoration: 'none', display: 'block', textAlign: 'center', marginTop: 8 }}>
                  + Kühlschrank scannen
                </Link>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
                <div className="prio-scan-invite">
                  <span><IconNavCamera size={16} /></span>
                  <span>{recentRecipes.length === 0 ? 'Nach deinem ersten Scan siehst du hier deine echten Lebensmittel.' : 'Nach deinem letzten Scan sind keine ablaufenden Lebensmittel da. 🎉'}</span>
                </div>
                <Link to="/kuehlschrank" className="prio-add-btn" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
                  + Kühlschrank scannen
                </Link>
                {recentRecipes.length === 0 && (!showDemoPrio ? (
                  <button
                    className="prio-add-btn"
                    style={{ background: 'transparent', border: '1px dashed var(--border)', color: 'var(--txt3)', cursor: 'pointer', fontSize: '13px' }}
                    onClick={() => setShowDemoPrio(true)}
                  >
                    Beispieldaten anzeigen
                  </button>
                ) : (
                  <div className="prio-list">
                    <div className="sec-header" style={{ marginBottom: 8 }}>
                      <span className="dash-demo-badge">Demo</span>
                      <button style={{ background: 'none', border: 'none', color: 'var(--txt3)', cursor: 'pointer', fontSize: '12px', marginLeft: 'auto' }} onClick={() => setShowDemoPrio(false)}>
                        <IconClose size={12} /> ausblenden
                      </button>
                    </div>
                    {[
                      { id: 'd1', name: 'Hackfleisch', days: 1 },
                      { id: 'd2', name: 'Joghurt', days: 3 },
                      { id: 'd3', name: 'Paprika', days: 5 },
                    ].map(({ id, name, days }) => {
                      const isToday      = days <= 0;
                      const isCrit       = days <= 2;
                      const urgencyClass = isToday ? 'prio-card--red' : days <= 3 ? 'prio-card--amber' : 'prio-card--green';
                      const pillClass    = isToday ? 'pill pill-solid prio-pill-today' : isCrit ? 'pill pill-red' : 'pill pill-amber';
                      const pillText     = isToday ? 'HEUTE!' : `NOCH ${days}T`;
                      return (
                        <div key={id} className={`prio-card ${urgencyClass}`}>
                          <div className="prio-normal">
                            <div className="prio-top">
                              <span className="prio-name">{name}</span>
                              <span className={pillClass} style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{pillText}</span>
                            </div>
                          </div>
                          <div className="prio-actions">
                            <button className="pa-btn pa-done" title="Verbraucht"><IconCheck size={15} /></button>
                            <button className="pa-btn pa-cal" title="Zum Kalender"><IconNavCalendar size={15} /></button>
                            <button className="pa-btn pa-del" title="Entfernen"><IconDelete size={15} /></button>
                          </div>
                        </div>
                      );
                    })}
                    <p className="dash-demo-hint">{DASHBOARD.demoHint}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Rechts: Eigene Rezepte ODER neueste öffentliche Rezepte als Inspiration */}
          {recentRecipes.length === 0 && !isGuest ? (
          <div>
            <div className="sec-header">
              <div className="sec-title">Was andere kochen</div>
              <Link to="/rezepte" className="sec-link">Alle entdecken →</Link>
            </div>
            <div className="dash-recipe-list">
              {newestRecipes.slice(0, 3).map(r => (
                <div key={r.id} className="dash-recipe-card">
                  {r.image_path
                    ? <img className="drc-img" src={r.image_path} alt={r.title} />
                    : <div className="drc-img drc-img-placeholder"><IconChefHat size={28} /></div>
                  }
                  <div className="drc-body">
                    <Link to={`/recipe/${r.id}`} className="drc-title">{r.title}</Link>
                    <div className="drc-chips-row">
                      {r.prep_time > 0 && <span className="drc-chip"><IconClock size={12} />{formatTime(r.prep_time)}</span>}
                      {r.category && <span className="drc-chip">{r.category}</span>}
                    </div>
                    <div className="drc-progress">
                      <Link to={`/recipe/${r.id}`} className="drc-btn">Öffnen<IconChevRight size={14} /></Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          ) : recentRecipes.length > 0 ? (
          <div>
            <div className="sec-header">
              <div className="sec-title">Zuletzt hinzugefügt</div>
              <Link to="/my-recipes" className="sec-link">Alle {recipeCount} →</Link>
            </div>
            <div className="dash-recipe-list">
              {recommended.map(r => (
                <div key={r.id} className="dash-recipe-card">
                  {r.image_path ? (
                    <img className="drc-img" src={r.image_path} alt={r.title} />
                  ) : (
                    <div className="drc-img drc-img-placeholder"><IconChefHat size={28} /></div>
                  )}
                  <div className="drc-body">
                    <Link to={`/recipe/${r.id}`} className="drc-title">{r.title}</Link>
                    <div className="drc-chips-row">
                      {r.prep_time > 0 && (
                        <span className="drc-chip"><IconClock size={12} />{formatTime(r.prep_time)}</span>
                      )}
                      {r.category && <span className="drc-chip">{r.category}</span>}
                    </div>
                    <div className="drc-progress">
                      <Link to={`/recipe/${r.id}`} className="drc-btn">Öffnen<IconChevRight size={14} /></Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          ) : null}

        </div>
      )}

      {/* ── Neueste Rezepte (systemweit, als Liste) ── */}
      {!loading && newestRecipes.length > 0 && (
        <div className="dash-recent">
          <div className="sec-header">
            <div className="sec-title">{DASHBOARD.neuesteEntdecken}</div>
            <Link to="/rezepte" className="sec-link">Alle entdecken →</Link>
          </div>
          <div className="recipe-list">
            {newestRecipes.map(r => (
              <div key={r.id} className="rr-wrap">
                <Link to={`/recipe/${r.id}`} className="rr-item">
                  <div className="rr-thumb">
                    {r.image_path
                      ? <img src={r.image_path} alt={r.title} />
                      : <IconChefHat size={24} />
                    }
                  </div>
                  <div className="rr-main">
                    <div className="rr-title">{r.title}</div>
                    <div className="rr-meta-row">
                      {r.category && <span className="cat-badge">{r.category}</span>}
                      {r.servings && (
                        <span className="rr-meta">
                          <IconUsers size={13} />
                          {r.servings} Port.
                        </span>
                      )}
                      {r.prep_time > 0 && (
                        <span className="rr-meta">
                          <IconClock size={13} />
                          {formatTime(r.prep_time)}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}


    </div>
  );
}
